import { DisplacementMapFeature } from '@kb3d/models/classes/features/displacement-map-feature';
import { FeatureGeometryProcessor, ProcessorData } from './base-processor';
import { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';
import type { WorkerProcessorHost } from '@kb3d/models/interfaces/worker-types';
import { ensureNormalsExist } from '../utils';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';

// This is passed to the worker as additional information needed
export type DisplacementMapWorkerPayload = {
    textureImageData: Uint8ClampedArray;
    textureWidth: number;
    textureHeight: number;
};

// This is processed and deserialized in the worker based on the worker payload
export type DisplacementMapDependencyData = {
    textureImageData: Uint8Array;
    textureWidth: number;
    textureHeight: number;
};

export class DisplacementMapGeometryProcessor extends FeatureGeometryProcessor<
    DisplacementMapFeature,
    DisplacementMapDependencyData
> {
    readWorkerPayload(host: WorkerProcessorHost, data: DisplacementMapWorkerPayload) {
        if (!data) {
            return null;
        }

        return {
            ...data,
            textureImageData: new Uint8Array(data.textureImageData),
        };
    }

    updateMeshGeometry(
        feature: DisplacementMapFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>,
        dependencyData: ProcessorData<DisplacementMapDependencyData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges, dependencyData);
        const data = dependencyData.data;
        if (!data) {
            return newVertexData;
        }

        const tempVertexData = new SubmeshVertexData();
        tempVertexData.copyFrom(newVertexData);
        if (tempVertexData.uvs) {
            const n = tempVertexData.uvs.length;
            for (let i = 1; i < n; i += 2) {
                tempVertexData.uvs[i] = 1 - tempVertexData.uvs[i];
            }

            const meshUpdated = new Mesh('ExistingMesh');
            tempVertexData.applyToMesh(meshUpdated, true);
            ensureNormalsExist(meshUpdated);

            this.displace(data, feature.minDisplacement, feature.maxDisplacement, meshUpdated);
            const meshVertexData = VertexData.ExtractFromMesh(meshUpdated);
            newVertexData.positions = meshVertexData.positions;
            newVertexData.normals = meshVertexData.normals;
            meshUpdated.dispose();
        }
        return newVertexData;
    }

    displace(
        imageData: DisplacementMapDependencyData,
        minDisplacement: number,
        maxDisplacement: number,
        parentMesh: Mesh
    ) {
        parentMesh.applyDisplacementMapFromBuffer(
            imageData.textureImageData,
            imageData.textureWidth,
            imageData.textureHeight,
            minDisplacement,
            maxDisplacement
        );
    }
}
